import React from 'react';
import { motion } from 'framer-motion';
import Container from '../layout/Container';
import FuturisticButton from '../ui/futuristic/FuturisticButton';

interface HeroProps {
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  imageSrc?: string;
  imageAlt?: string;
  backgroundClass?: string;
  backgroundType?: 'gradient' | 'image' | 'color';
}

/**
 * Hero section component for page headers with call-to-action — neumorphic style
 */
const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  ctaText,
  ctaLink,
  secondaryCtaText,
  secondaryCtaLink,
  imageSrc,
  imageAlt,
}) => {
  return (
    <div className="relative overflow-hidden bg-neu-bg pt-28 pb-14">
      {/* Decorative neumorphic concentric circles */}
      <div
        className="pointer-events-none absolute top-0 right-0 h-full flex items-center opacity-50 overflow-hidden"
        style={{ width: '38%' }}
      >
        <div
          className="relative flex items-center justify-center"
          style={{ width: 380, height: 380, marginLeft: 'auto', marginRight: '-80px' }}
        >
          <div
            className="absolute rounded-full"
            style={{
              width: 380,
              height: 380,
              boxShadow: '10px 10px 22px rgb(163,177,198,0.5), -10px -10px 22px rgba(255,255,255,0.4)',
              background: '#E0E5EC',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: 260,
              height: 260,
              boxShadow: 'inset 7px 7px 14px rgb(163,177,198,0.6), inset -7px -7px 14px rgba(255,255,255,0.5)',
              background: '#E0E5EC',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: 140,
              height: 140,
              boxShadow: '7px 7px 14px rgb(163,177,198,0.6), -7px -7px 14px rgba(255,255,255,0.5)',
              background: '#E0E5EC',
            }}
          />
          <div
            className="absolute rounded-full animate-float"
            style={{
              width: 56,
              height: 56,
              background: 'linear-gradient(135deg, #6C63FF 0%, #8B84FF 100%)',
              boxShadow: '0 4px 16px rgba(108,99,255,0.45)',
            }}
          />
        </div>
      </div>

      {/* Hero content */}
      <Container>
        <motion.div
          className="relative z-10 flex flex-col lg:flex-row items-center gap-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex-1 text-center lg:text-left">
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-neu-text mb-6 leading-tight">
              {title}
            </h1>
            <p className="text-lg md:text-xl text-neu-muted mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              {subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <FuturisticButton
                variant="accent"
                size="lg"
                onClick={() => { window.location.href = ctaLink; }}
              >
                {ctaText}
              </FuturisticButton>
              {secondaryCtaText && secondaryCtaLink && (
                <FuturisticButton
                  variant="outline"
                  size="lg"
                  onClick={() => { window.location.href = secondaryCtaLink; }}
                >
                  {secondaryCtaText}
                </FuturisticButton>
              )}
            </div>
          </div>

          {imageSrc && (
            <div className="hidden lg:flex flex-1 justify-end">
              <div className="relative max-w-md w-full">
                <img
                  src={imageSrc}
                  alt={imageAlt || 'Hero image'}
                  className="w-full h-auto rounded-neu shadow-neu-extruded"
                />
              </div>
            </div>
          )}
        </motion.div>
      </Container>
    </div>
  );
};

export default Hero;